import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ShieldCheck, Camera, Warning, CheckCircle, XCircle, UploadSimple } from '@phosphor-icons/react';
import AuthImage from '@/components/AuthImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ANSWERS = [
  { v: 'conforme', label: 'Conforme', cls: 'peer-checked:bg-emerald-600 peer-checked:text-white peer-checked:border-emerald-600' },
  { v: 'non_conforme', label: 'Non conforme', cls: 'peer-checked:bg-red-600 peer-checked:text-white peer-checked:border-red-600' },
  { v: 'non_applicable', label: 'N/A', cls: 'peer-checked:bg-zinc-600 peer-checked:text-white peer-checked:border-zinc-600' },
];
const SIDE = { left: 'GAUCHE', right: 'DROIT' };
const fmt = (d) => (d ? new Date(d).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

// Formulaire CQ-01 d'un dispositif : critères (rien de pré-coché), double vérification écouteur, photo stockée obligatoire.
function Cq01Form({ ident, tpl, onDone }) {
  const [answers, setAnswers] = useState({});
  const [checks, setChecks] = useState([{ observed_ref: '', matches: null }, { observed_ref: '', matches: null }]);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const criteria = tpl.criteria;
  const receiverNeeded = tpl.receiver_checks_required === 2 && answers.ecouteur_reference !== 'non_applicable';
  const allAnswered = criteria.every(c => answers[c.key]);
  const checksOk = !receiverNeeded || checks.every(c => c.observed_ref.trim() && typeof c.matches === 'boolean');
  const canSubmit = allAnswered && checksOk && photos.length > 0 && !submitting && !uploading;

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData(); fd.append('file', f);
      try {
        const r = await axios.post(`${API}/devices/${encodeURIComponent(ident)}/cq01/photos`, fd);
        setPhotos(p => [...p, r.data]);
        toast.success('Photo enregistrée sur le serveur');
      } catch (err) { toast.error(err.response?.data?.detail || `Échec du téléversement de ${f.name} — la photo n'est pas prise en compte`); }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/devices/${encodeURIComponent(ident)}/cq01`, { answers, receiver_checks: receiverNeeded ? checks : [], photo_ids: photos.map(p => p.id), comment });
      onDone(r.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur CQ-01'); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-4" data-testid={`cq01-form-${ident}`}>
      <div className="border border-zinc-200 rounded-sm overflow-hidden">
        <div className="grid grid-cols-[1.1fr_1.2fr_auto] gap-2 px-3 py-1.5 bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500 border-b">
          <span>Point à vérifier</span><span>Attendu (commande)</span><span>Réponse</span>
        </div>
        {criteria.map(c => (
          <div key={c.key} className={`grid grid-cols-[1.1fr_1.2fr_auto] gap-2 px-3 py-2 border-b last:border-b-0 items-center ${c.applicable === false ? 'bg-zinc-50/60' : ''}`} data-testid={`cq01-criterion-${c.key}`}>
            <span className="text-sm font-medium text-zinc-800">{c.label}</span>
            <span className={`text-xs ${c.expected ? 'text-zinc-700' : 'italic text-zinc-400'}`} data-testid={`cq01-expected-${c.key}`}>{c.expected || (c.applicable === false ? 'Sans objet pour ce dispositif' : 'Non renseigné')}</span>
            <div className="flex gap-1">
              {ANSWERS.filter(a => a.v !== 'non_applicable' || c.na_allowed).map(a => (
                <label key={a.v} className="cursor-pointer">
                  <input type="radio" name={`${ident}-${c.key}`} value={a.v} checked={answers[c.key] === a.v} onChange={() => setAnswers(s => ({ ...s, [c.key]: a.v }))} className="peer sr-only" data-testid={`cq01-${c.key}-${a.v}`} />
                  <span className={`inline-block text-[11px] px-2 py-1 border border-zinc-300 rounded-sm bg-white text-zinc-700 transition-colors ${a.cls}`}>{a.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      {receiverNeeded && (
        <div className="border border-blue-200 bg-blue-50 rounded-sm p-3 space-y-2" data-testid="cq01-receiver-checks">
          <p className="text-sm font-medium text-blue-900">Écouteur — deux vérifications explicites de la référence</p>
          <p className="text-xs text-blue-800">Référence attendue : <span className="font-mono font-semibold" data-testid="cq01-expected-receiver">{tpl.expected_receiver_ref || 'Non renseignée sur la commande'}</span></p>
          {checks.map((ch, i) => (
            <div key={i} className="grid sm:grid-cols-[auto_1fr_auto] gap-2 items-center" data-testid={`cq01-receiver-check-${i + 1}`}>
              <span className="text-xs font-semibold text-blue-900">Vérif. {i + 1}</span>
              <Input value={ch.observed_ref} onChange={e => setChecks(cs => cs.map((c, j) => j === i ? { ...c, observed_ref: e.target.value } : c))} placeholder="Référence lue sur l'écouteur" className="rounded-sm bg-white h-8 text-sm" data-testid={`cq01-receiver-ref-${i + 1}`} />
              <div className="flex gap-1">
                <button type="button" onClick={() => setChecks(cs => cs.map((c, j) => j === i ? { ...c, matches: true } : c))} className={`text-[11px] px-2 py-1 border rounded-sm ${ch.matches === true ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white border-zinc-300'}`} data-testid={`cq01-receiver-match-${i + 1}`}>Correspond</button>
                <button type="button" onClick={() => setChecks(cs => cs.map((c, j) => j === i ? { ...c, matches: false } : c))} className={`text-[11px] px-2 py-1 border rounded-sm ${ch.matches === false ? 'bg-red-600 text-white border-red-600' : 'bg-white border-zinc-300'}`} data-testid={`cq01-receiver-mismatch-${i + 1}`}>Ne correspond pas</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className={`border rounded-sm p-3 ${photos.length ? 'border-emerald-300 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`} data-testid="cq01-photo-box">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="text-sm font-medium flex items-center gap-1"><Camera size={16} />Photo finale du dispositif contrôlé (obligatoire)</p>
          <label className="cursor-pointer">
            <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={upload} data-testid="cq01-photo-input" />
            <span className="inline-flex items-center gap-1 text-xs px-3 py-1.5 bg-zinc-900 text-white rounded-sm"><UploadSimple size={14} />{uploading ? 'Téléversement…' : 'Téléverser une photo'}</span>
          </label>
        </div>
        {photos.length === 0 ? <p className="text-xs text-amber-800 mt-1">Aucune photo stockée — un aperçu local ou un téléversement échoué ne suffit pas.</p> : (
          <div className="flex gap-2 mt-2 flex-wrap" data-testid="cq01-photos-stored">
            {photos.map(p => (
              <div key={p.id} className="relative" data-testid={`cq01-photo-${p.id}`}>
                <AuthImage url={`${API}/devices/${encodeURIComponent(ident)}/cq01/photos/${p.id}`} alt={p.filename} className="w-20 h-20 object-cover rounded-sm border" />
                <span className="absolute -top-1 -right-1 bg-emerald-600 text-white rounded-full"><CheckCircle size={16} weight="fill" /></span>
              </div>
            ))}
            <p className="text-[11px] text-emerald-800 self-end">{photos.length} photo(s) enregistrée(s) sur le serveur, rattachée(s) à {tpl.device.hamac_id}</p>
          </div>
        )}
      </div>

      <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Observations (optionnel)" className="rounded-sm text-sm" rows={2} data-testid="cq01-comment" />
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[11px] text-zinc-500">{criteria.filter(c => answers[c.key]).length}/{criteria.length} critères renseignés{receiverNeeded ? ` · vérif. écouteur ${checks.filter(c => c.observed_ref.trim() && typeof c.matches === 'boolean').length}/2` : ''} · {photos.length} photo(s)</p>
        <Button onClick={submit} disabled={!canSubmit} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="cq01-submit-btn">{submitting ? 'Enregistrement…' : 'Valider le CQ-01'}</Button>
      </div>
    </div>
  );
}

function DevicePanel({ device, onStateChange }) {
  const ident = device.hamac_id || device.serial_number;
  const [tpl, setTpl] = useState(null);
  const [result, setResult] = useState(null);
  const [corrective, setCorrective] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/devices/${encodeURIComponent(ident)}/cq01/template`); setTpl(r.data); onStateChange(device.id, r.data.control_state.ready); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erreur chargement CQ-01'); }
  }, [ident]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  if (!tpl) return <p className="text-sm text-zinc-500">Chargement…</p>;
  const st = tpl.control_state;
  const openNc = (tpl.non_conformities || []).filter(n => n.status === 'open');
  const specs = tpl.device.specs || {};

  const saveCorrective = async (nc) => {
    setSaving(true);
    try {
      await axios.post(`${API}/devices/${encodeURIComponent(ident)}/non-conformities/${nc.id}/corrective-action`, { description: corrective });
      toast.success('Action corrective enregistrée — nouveau CQ-01 complet requis');
      setCorrective(''); setResult(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-3" data-testid={`cq01-panel-${device.side}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap text-xs text-zinc-600">
        <span><span className="font-mono font-semibold text-zinc-900">{tpl.device.hamac_id}</span> · {SIDE[device.side]} · {tpl.device.serial_number}{tpl.device.material_lot_number ? ` · lot ${tpl.device.material_lot_number}` : ''}{specs.marque ? ` · ${specs.marque} ${specs.modele || ''}` : ''}</span>
        <span data-testid={`cq01-attempts-${device.side}`}>{(tpl.attempts || []).length} tentative(s) CQ-01</span>
      </div>

      {st.ready && (
        <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-300 rounded-sm text-emerald-900 text-sm" data-testid={`cq01-ready-${device.side}`}>
          <ShieldCheck size={18} weight="fill" /><span><strong>CQ-01 conforme</strong> — contrôlé le {fmt((tpl.attempts.at(-1) || {}).checked_at)} par {(tpl.attempts.at(-1) || {}).operator_name}. Photo finale rattachée. Dispositif libérable.</span>
        </div>
      )}

      {!st.ready && result?.attempt?.result === 'non_conforme' && (
        <div className="p-3 bg-red-50 border border-red-300 rounded-sm text-sm text-red-900 space-y-1" data-testid={`cq01-result-nc-${device.side}`}>
          <p className="flex items-center gap-1 font-semibold"><XCircle size={18} weight="fill" />CQ-01 NON CONFORME — tentative {result.attempt.attempt_number}</p>
          <p>Critère(s) : {result.attempt.failed_labels.join(', ')}</p>
          <p className="text-xs">Contrôle échoué enregistré, non-conformité créée, statut « Embout prêt » et libération bloqués.</p>
        </div>
      )}

      {!st.ready && openNc.length > 0 && openNc.map(nc => (
        <div key={nc.id} className="p-3 border border-amber-300 bg-amber-50 rounded-sm space-y-2" data-testid={`cq01-nc-${nc.id}`}>
          <p className="text-sm font-medium text-amber-900 flex items-center gap-1"><Warning size={16} />Non-conformité ouverte — {fmt(nc.created_at)} · {nc.operator_name}</p>
          <p className="text-xs text-amber-900">{nc.description}</p>
          <Textarea value={corrective} onChange={e => setCorrective(e.target.value)} placeholder="Action corrective réalisée (obligatoire avant un nouveau contrôle)" className="rounded-sm text-sm bg-white" rows={2} data-testid="cq01-corrective-input" />
          <Button size="sm" onClick={() => saveCorrective(nc)} disabled={corrective.trim().length < 5 || saving} className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white" data-testid="cq01-corrective-submit">Enregistrer l'action corrective</Button>
        </div>
      ))}

      {!st.ready && openNc.length === 0 && (
        <>
          {st.last_result && <p className="text-xs text-zinc-600" data-testid={`cq01-reasons-${device.side}`}>{st.reasons.join(' · ')} — nouveau contrôle complet avec photo finale actualisée.</p>}
          <Cq01Form key={(tpl.attempts || []).length} ident={ident} tpl={tpl} onDone={(r) => { setResult(r); if (r.attempt.result === 'conforme') toast.success('CQ-01 conforme'); else toast.error('CQ-01 non conforme — non-conformité créée'); load(); }} />
        </>
      )}
    </div>
  );
}

// Ouvert au clic « Prêt » : « Embout prêt » n'est enregistré qu'après réussite du CQ-01 de chaque dispositif.
export default function Cq01Dialog({ orderId, order, open, onOpenChange, onReady }) {
  const [devices, setDevices] = useState([]);
  const [readyMap, setReadyMap] = useState({});
  const [active, setActive] = useState(0);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    if (!open || !orderId) return;
    setDevices([]); setReadyMap({}); setActive(0);
    axios.get(`${API}/devices`, { params: { order_id: orderId } }).then(r => setDevices((r.data.items || []).sort((a, b) => (a.side === 'right' ? -1 : 1)))).catch(e => toast.error(e.response?.data?.detail || 'Erreur'));
  }, [open, orderId]);

  const allReady = devices.length > 0 && devices.every(d => readyMap[d.id]);

  const markReady = async () => {
    setFinishing(true);
    try {
      const r = await axios.post(`${API}/orders/${orderId}/ready`);
      toast.success('Embout prêt — dispositifs libérés');
      onReady?.(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setFinishing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-[96vw] sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="cq01-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} />Contrôle qualité final CQ-01</DialogTitle>
          <DialogDescription>{order?.patient_reference ? `Réf. patient ${order.patient_reference} · ` : ''}Le statut « Embout prêt » n'est enregistré qu'après un CQ-01 conforme (photo finale stockée) pour chaque dispositif.</DialogDescription>
        </DialogHeader>
        {devices.length === 0 ? <p className="text-sm italic text-zinc-400">Aucune fiche dispositif pour cette commande.</p> : (
          <>
            <div className="flex gap-1 border-b border-zinc-200" data-testid="cq01-device-tabs">
              {devices.map((d, i) => (
                <button key={d.id} type="button" onClick={() => setActive(i)} className={`px-3 py-2 text-sm border-b-2 -mb-px flex items-center gap-1 ${active === i ? 'border-zinc-900 font-semibold' : 'border-transparent text-zinc-500'}`} data-testid={`cq01-tab-${d.side}`}>
                  {readyMap[d.id] ? <CheckCircle size={14} weight="fill" className="text-emerald-600" /> : <Warning size={14} className="text-amber-600" />}
                  {SIDE[d.side]} · {d.hamac_id}
                </button>
              ))}
            </div>
            {devices.map((d, i) => <div key={d.id} className={active === i ? '' : 'hidden'}><DevicePanel device={d} onStateChange={(id, ready) => setReadyMap(m => ({ ...m, [id]: ready }))} /></div>)}
          </>
        )}
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Fermer</Button>
          <Button onClick={markReady} disabled={!allReady || finishing} className="rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="cq01-mark-ready-btn">{finishing ? 'Enregistrement…' : 'Marquer « Embout prêt »'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
